import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Actions, ofType } from '@ngrx/effects';
import { switchMap, tap } from 'rxjs/operators';
import * as fromTimerActions from '../../../../core/state/time-blocks/timer/timer.actions';
import { TimerStatus } from '../../../../features/calendar/calendar-types';
import { ITimeBlockComponentItem } from '../../../../features/calendar/components/time-block/types/time-block-component-item-types';
import { TimeEntryConverter } from '../converter/time-entry.converter';
import { TimeBlockListEntry } from '../time-enty-list-types';
import { timeEntryListTrackerStarted } from './time-entries-data.actions';

type State = {
  entries: TimeBlockListEntry[];
  activeTimerTimeBlock: ITimeBlockComponentItem;
  activeTrackerTimeBlock: ITimeBlockComponentItem;
};

const initialState: State = {
  entries: [],
  activeTimerTimeBlock: null,
  activeTrackerTimeBlock: null,
};

@Injectable()
export class TimeEntriesStore extends ComponentStore<State> {
  // selectors
  readonly entries$ = this.select((s) => s.entries);
  readonly activeTimerTimeBlock$ = this.select((s) => s.activeTimerTimeBlock);
  readonly activeTrackerTimeBlock$ = this.select((s) => s.activeTrackerTimeBlock);

  // updaters
  readonly setTimeBlockEntries = this.updater((state, entries: TimeBlockListEntry[]) => ({
    ...state,
    entries,
  }));

  readonly addSortedTimeBlockEntry = this.updater((state, entry: TimeBlockListEntry) => {
    const entries = [...state.entries, entry].sort((first, second) => +second.from - +first.from);

    return {
      ...state,
      entries,
    };
  });

  readonly setActiveTimerTimeBlockEntry = this.updater((state, entry: ITimeBlockComponentItem) => ({
    ...state,
    activeTimerTimeBlock: entry,
  }));

  readonly setActiveTrackerTimeBlockEntry = this.updater(
    (state, entry: ITimeBlockComponentItem) => ({
      ...state,
      activeTrackerTimeBlock: entry,
    }),
  );

  readonly updateTimeBlockEntry = this.updater((state, updated: TimeBlockListEntry) => ({
    ...state,
    entries: state.entries.map((existing) => (existing.id === updated.id ? updated : existing)),
  }));

  readonly replaceTimeBlockEntry = this.updater(
    (
      state,
      { original, updated }: { original: TimeBlockListEntry; updated: TimeBlockListEntry },
    ) => ({
      ...state,
      entries: state.entries.map((existing) => (existing.id === original.id ? updated : existing)),
    }),
  );

  readonly deleteInStore = this.updater((state, deletedId: number) => ({
    ...state,
    entries: state.entries.filter((entry) => entry.id !== deletedId),
  }));

  // effect: react to global NgRx action and update local store
  private readonly listenToTimerStartOrStopEffect = this.effect<void>((trigger$) =>
    trigger$.pipe(
      switchMap(() =>
        this.actions$.pipe(
          ofType(fromTimerActions.timerPosted, timeEntryListTrackerStarted),
          tap((response) => {
            if (response.type === fromTimerActions.timerPosted.type) {
              const payload = response.payload;
              const entry = TimeEntryConverter.toTimeBlockListEntry(payload.generatedTimeBlock);

              if (payload.status === TimerStatus.Start) {
                this.addSortedTimeBlockEntry(entry);
                this.setActiveTimerTimeBlockEntry(payload.generatedTimeBlock);
              } else {
                this.setActiveTimerTimeBlockEntry(null);
                this.updateTimeBlockEntry(entry);
              }

              return;
            }

            // Here, response is timeEntryListTrackerStarted. Remark: There is no need to conduct a timer post http request since
            // the tracker stops the timer time block automatically.
            const oldActiveTimeEntry = response.payload.activeTimerEntry;
            this.setActiveTimerTimeBlockEntry(null);
            this.updateTimeBlockEntry(oldActiveTimeEntry);
          }),
        ),
      ),
    ),
  );

  constructor(private readonly actions$: Actions) {
    super(initialState);
    // start listening once store is created
    this.listenToTimerStartOrStopEffect();
  }
}
